// src/hooks/useAxiosInterceptors.ts
import { useEffect, useRef, useState } from 'react'
import { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios'

import { axiosInstance, paymentAxiosInstance } from '../api/axios'
import { isTokenExpired, refreshToken } from './useAccount'
import { JWT_KEY, User_ID_KEY } from '../constants/localstorage'

// Extend AxiosRequestConfig to include our custom requireAuth property
interface CustomRequestConfig extends InternalAxiosRequestConfig {
  requireAuth?: boolean
}

// Get current valid token (if any)
export const getValidToken = (): string | null => {
  try {
    const token = localStorage.getItem(JWT_KEY)
    if (!token) return null

    // Parse the token from JSON string format
    const parsedToken = JSON.parse(token)

    if (isTokenExpired(parsedToken)) {
      // If token is expired, remove it
      localStorage.removeItem(JWT_KEY)
      localStorage.removeItem(User_ID_KEY)
      return null
    }
    refreshToken(parsedToken)
    return parsedToken
  } catch (error) {
    console.error('Error reading token from localStorage:', error)
    return null
  }
}

// Helper function to setup interceptors for any axios instance
const setupInterceptors = (
  instance: AxiosInstance,
  requestRef: React.MutableRefObject<number | null>,
  responseRef: React.MutableRefObject<number | null>,
): void => {
  // Clear previous interceptors if they exist
  if (requestRef.current !== null) {
    instance.interceptors.request.eject(requestRef.current)
  }
  if (responseRef.current !== null) {
    instance.interceptors.response.eject(responseRef.current)
  }

  // Request interceptor
  requestRef.current = instance.interceptors.request.use(
    (config: CustomRequestConfig) => {
      // Use requireAuth property to determine if authentication is needed
      const requireAuth = config.requireAuth !== false

      // Skip auth for certain endpoints or when explicitly not required
      if (config.url && !requireAuth) {
        return config
      }

      // Always read fresh token from localStorage
      const token = getValidToken()
      if (token) {
        config.headers = config.headers || {}
        config.headers.Authorization = `Bearer ${token}`
        return config
      }

      return Promise.reject(new Error('JWT expired'))
    },
    (error: AxiosError) => Promise.reject(error),
  )

  // Response interceptor
  responseRef.current = instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        // Clear token on auth failure
        localStorage.removeItem(JWT_KEY)
        localStorage.removeItem(User_ID_KEY)
      }
      return Promise.reject(error)
    },
  )
}

export const useAxiosInterceptors = (): boolean => {
  const [isInterceptorsInitialized, setIsInterceptorsInitialized] = useState<boolean>(false)

  // Refs for main API instance
  const requestInterceptorRef = useRef<number | null>(null)
  const responseInterceptorRef = useRef<number | null>(null)

  // Refs for payment API instance
  const paymentRequestInterceptorRef = useRef<number | null>(null)
  const paymentResponseInterceptorRef = useRef<number | null>(null)

  // Initialize interceptors only once
  useEffect(() => {
    // Setup interceptors for main API
    setupInterceptors(axiosInstance, requestInterceptorRef, responseInterceptorRef)

    // Setup interceptors for payment API
    setupInterceptors(
      paymentAxiosInstance,
      paymentRequestInterceptorRef,
      paymentResponseInterceptorRef,
    )

    setIsInterceptorsInitialized(true)

    // Cleanup on unmount
    return () => {
      // Cleanup main API interceptors
      if (requestInterceptorRef.current !== null) {
        axiosInstance.interceptors.request.eject(requestInterceptorRef.current)
      }
      if (responseInterceptorRef.current !== null) {
        axiosInstance.interceptors.response.eject(responseInterceptorRef.current)
      }

      // Cleanup payment API interceptors
      if (paymentRequestInterceptorRef.current !== null) {
        paymentAxiosInstance.interceptors.request.eject(paymentRequestInterceptorRef.current)
      }
      if (paymentResponseInterceptorRef.current !== null) {
        paymentAxiosInstance.interceptors.response.eject(paymentResponseInterceptorRef.current)
      }
    }
  }, []) // Empty dependency array - only run once

  return isInterceptorsInitialized
}
